import TextInputWithDropdown from './TextInputWithDropdown.jsx';
import TextInput from './TextInput';
import Select from './Select';
import { useEffect, useState } from 'react';
import { MINUTES_IN_A_DAY } from '~/lib/DateTime.js';
import ExperimentalBadge from '~/components/ExperimentalBadge.jsx';
import ErrorMessage from '~/components/ErrorMessage';

const dropdownOptions = [
    { value: 'minutes', label: 'minutes' },
    { value: 'hours', label: 'hours' },
    { value: 'days', label: 'days' },
];

const dropdownOptionsWithVariableMultiday = [
    { value: 'minutes', label: 'minutes' },
    { value: 'hours', label: 'hours' },
    { value: 'days', label: 'days' },
    { value: 'variable-multiday', label: 'day range (hotel, rental, RV park, etc.)' },
];

const multipliers = {
    minutes: 1,
    hours: 60,
    days: 60 * 24,
};

const DurationInput = ({
    value,
    onChange,
    id,
    label,
    className,
    autoFocus,
    placeholder,
    excludeOptions,
    allowVariableMultiday = false,
    minDays,
    maxDays,
    onMinDaysChange,
    onMaxDaysChange,
}) => {
    const [dropdownValue, setDropdownValue] = useState();
    const multiplier = multipliers[dropdownValue] || 1;
    const [loadedAt, setLoadedAt] = useState(new Date());

    useEffect(() => {
        const now = new Date();

        // If we loaded within 100ms of the value being set then assume it's the initial value
        if (now - loadedAt < 500 && value) {
            // Check if this is variable multiday (value is -1 or a positive integer when in variable multiday mode)
            if (
                allowVariableMultiday &&
                (value === -1 || (value > 0 && maxDays))
            ) {
                setDropdownValue('variable-multiday');
            } else if (value % multipliers.days === 0) {
                setDropdownValue('days');
            } else if (value % multipliers.hours === 0) {
                setDropdownValue('hours');
            } else {
                setDropdownValue('minutes');
            }
        }
    }, [value, allowVariableMultiday, minDays]);

    const textValue =
        dropdownValue === 'variable-multiday' ? 0 : value / multiplier;

    // Determine which options to show
    const availableOptions = allowVariableMultiday
        ? dropdownOptionsWithVariableMultiday
        : dropdownOptions;
    const filteredOptions = availableOptions.filter(
        opt => !excludeOptions || !excludeOptions.includes(opt.value),
    );

    // Handle variable multiday case
    if (dropdownValue === 'variable-multiday') {
        return (
            <div className={className}>
                <div className="flex items-center space-x-4">
                    <Select
                        id={id}
                        label={label}
                        value={dropdownValue}
                        onChange={newValue => {
                            setDropdownValue(newValue);
                            if (newValue === 'variable-multiday') {
                                // If we have minDays, use that as the duration, otherwise use 1
                                onMinDaysChange(
                                    minDays ? parseInt(minDays) : 1,
                                );
                            } else {
                                // Switch back to normal duration input
                                onChange(1 * multipliers[newValue]); // Default to 1 unit
                                onMaxDaysChange('');
                            }
                        }}
                        options={filteredOptions.reduce((acc, opt) => {
                            acc[opt.value] = opt.label;
                            return acc;
                        }, {})}
                    />
                    <ExperimentalBadge className="[margin-top:20px]" />
                </div>

                {/* Min/Max days inputs for variable multiday */}
                <div className="flex gap-4 mt-4">
                    <TextInput
                        id={`${id}-min-days`}
                        label="Min Days"
                        className="flex-1"
                        value={minDays || ''}
                        onChange={onMinDaysChange}
                        placeholder="1"
                    />
                    <TextInput
                        id={`${id}-max-days`}
                        label="Max Days"
                        className="flex-1"
                        value={maxDays || ''}
                        onChange={onMaxDaysChange}
                        placeholder=""
                    />
                </div>
                {!!minDays && !!maxDays && minDays > maxDays && (
                    <ErrorMessage>
                        Min days must be less than max days
                    </ErrorMessage>
                )}
                {!minDays && (
                    <ErrorMessage>
                        Please enter a minimum number of days that someone can book for.
                    </ErrorMessage>
                )}
                {!maxDays && (
                    <ErrorMessage>
                        Please enter a maximum number of days that someone can book for.
                    </ErrorMessage>
                )}
            </div>
        );
    }

    // Normal duration input
    return (
        <TextInputWithDropdown
            id={id}
            label={label}
            className={className}
            textValue={textValue || '0'}
            autoFocus={autoFocus}
            placeholder={placeholder}
            setTextValue={newValue => onChange(parseInt(newValue) * multiplier)}
            dropdownValue={dropdownValue}
            dropdownClassName="max-w-24"
            setDropdownValue={newValue => {
                setDropdownValue(newValue);
                if (newValue === 'variable-multiday') {
                    // If we have minDays, use that as the duration, otherwise use -1
                    onMinDaysChange(minDays ? parseInt(minDays) : 1);
                    if (!maxDays) {
                        // Default to 30 days max
                        onMaxDaysChange(30);
                    }
                } else {
                    onChange(parseInt(textValue) * multipliers[newValue]);
                    if (maxDays) {
                        // Clear max days
                        onMaxDaysChange('');
                    }
                }
            }}
            options={filteredOptions}
        />
    );
};

export default DurationInput;
